import * as React from 'react';

import { cn } from './lib/cn';

export type SpinnerSize = 'sm' | 'md' | 'lg';

const SIZES: Record<SpinnerSize, string> = {
  sm: 'h-spinner-sm w-spinner-sm',
  md: 'h-spinner-md w-spinner-md',
  lg: 'h-spinner-lg w-spinner-lg',
};

export interface SpinnerProps extends React.HTMLAttributes<HTMLSpanElement> {
  size?: SpinnerSize;
}

/**
 * A bare busy indicator — no label and no live region.
 *
 * It is `aria-hidden` on purpose: the control that owns the wait announces it
 * (a `<Button loading>` sets `aria-busy`), and a spinner that announced itself
 * as well would say the same thing twice. If you reach for a Spinner outside
 * such a control, give the surrounding element the announcement.
 *
 * `motion-reduce:animate-none` because a perpetual rotation is exactly what
 * that preference is about.
 */
const Spinner = React.forwardRef<HTMLSpanElement, SpinnerProps>(({ className, size = 'md', ...props }, ref) => (
  <span
    ref={ref}
    aria-hidden="true"
    className={cn(
      'inline-block shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent motion-reduce:animate-none',
      SIZES[size],
      className,
    )}
    {...props}
  />
));
Spinner.displayName = 'Spinner';

export { Spinner };
